/** Every date and time people see is Mountain Time (America/Denver). Stored values are UTC. */
export const TIME_ZONE = "America/Denver";

const dateTime = new Intl.DateTimeFormat("en-US", {
  timeZone: TIME_ZONE,
  month: "short",
  day: "numeric",
  year: "numeric",
  hour: "numeric",
  minute: "2-digit",
  timeZoneName: "short",
});
const dateOnly = new Intl.DateTimeFormat("en-US", { timeZone: TIME_ZONE, month: "short", day: "numeric", year: "numeric" });
const usd = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" });

/** "Oct 26, 2026, 3:04 PM MDT" */
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : dateTime.format(d);
}

/** "Oct 26, 2026" (calendar dates such as birthdays are formatted as-is, not shifted). */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) {
    const [y, m, d] = iso.split("-").map(Number);
    return new Intl.DateTimeFormat("en-US", { timeZone: "UTC", month: "short", day: "numeric", year: "numeric" }).format(new Date(Date.UTC(y, m - 1, d)));
  }
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : dateOnly.format(d);
}

/** Integer cents to "$1,234.56". */
export function formatCents(cents: number | null | undefined): string {
  if (cents === null || cents === undefined) return "—";
  return usd.format(cents / 100);
}

/** Display name: "First Last", falling back to email or #id. */
export function displayName(c: { first_name?: string | null; last_name?: string | null; id?: number; email?: string }): string {
  const n = [c.first_name, c.last_name].filter(Boolean).join(" ").trim();
  return n || c.email || (c.id ? `#${c.id}` : "(no name)");
}

const intFmt = new Intl.NumberFormat("en-US");
const timeOnly = new Intl.DateTimeFormat("en-US", { timeZone: TIME_ZONE, hour: "numeric", minute: "2-digit" });
const shortDay = new Intl.DateTimeFormat("en-US", { timeZone: TIME_ZONE, month: "short", day: "numeric" });
const weekdayDay = new Intl.DateTimeFormat("en-US", { timeZone: TIME_ZONE, weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

/** 12345 -> "12,345" */
export function formatInt(n: number | null | undefined): string {
  return n === null || n === undefined ? "—" : intFmt.format(n);
}

/** 0.4231 -> "42.3%" (fractions from the API). Small rates keep two decimals. */
export function formatPercent(fraction: number | null | undefined, digits?: number): string {
  if (fraction === null || fraction === undefined || Number.isNaN(fraction)) return "—";
  const pct = fraction * 100;
  const d = digits ?? (pct !== 0 && Math.abs(pct) < 1 ? 2 : 1);
  return `${pct.toFixed(d)}%`;
}

/** "3:04 PM" in Mountain Time. */
export function formatTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : timeOnly.format(d);
}

/** "Oct 26" in Mountain Time. */
export function formatShortDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : shortDay.format(d);
}

/** "Mon, Oct 26, 3:04 PM" in Mountain Time (schedules). */
export function formatWhen(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : weekdayDay.format(d) + " MT";
}

/** "5 min ago", "3 hours ago", "2 days ago", else the date. */
export function formatAgo(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return "—";
  const d = new Date(iso);
  const s = Math.round((now.getTime() - d.getTime()) / 1000);
  if (Number.isNaN(s)) return "—";
  if (s < 0) return formatDateTime(iso);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} hour${s < 7200 ? "" : "s"} ago`;
  if (s < 86400 * 14) return `${Math.floor(s / 86400)} day${s < 172800 ? "" : "s"} ago`;
  return formatDate(iso);
}

/** Today's date in Mountain Time as YYYY-MM-DD (for date inputs and API filters). */
export function mountainDay(d: Date = new Date()): string {
  const parts = new Intl.DateTimeFormat("en-CA", { timeZone: TIME_ZONE, year: "numeric", month: "2-digit", day: "2-digit" }).format(d);
  return parts; // en-CA formats as YYYY-MM-DD
}

/** Adds days to a YYYY-MM-DD calendar date. */
export function addDays(day: string, n: number): string {
  const [y, m, dd] = day.split("-").map(Number);
  const t = new Date(Date.UTC(y, m - 1, dd + n));
  return t.toISOString().slice(0, 10);
}

/** Bytes to "1.2 MB". */
export function formatBytes(n: number | null | undefined): string {
  if (n === null || n === undefined) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

/** Offset of Mountain Time from UTC, in minutes, at a given instant (-360 in summer, -420 in winter). */
function mountainOffsetMinutes(at: Date): number {
  const part = new Intl.DateTimeFormat("en-US", { timeZone: TIME_ZONE, timeZoneName: "shortOffset" }).formatToParts(at).find((p) => p.type === "timeZoneName")?.value ?? "GMT-7";
  const m = /GMT([+-])(\d{1,2})(?::(\d{2}))?/.exec(part);
  if (!m) return -420;
  return (m[1] === "-" ? -1 : 1) * (Number(m[2]) * 60 + Number(m[3] ?? 0));
}

/** A Mountain Time wall-clock day ("2026-10-27") and time ("09:00") as a UTC ISO string. */
export function mountainToISO(day: string, time: string): string {
  const [y, mo, d] = day.split("-").map(Number);
  const [h, mi] = time.split(":").map(Number);
  const guess = Date.UTC(y, mo - 1, d, h, mi);
  const off = mountainOffsetMinutes(new Date(guess - mountainOffsetMinutes(new Date(guess)) * 60_000));
  return new Date(guess - off * 60_000).toISOString();
}

/** A stored instant as Mountain wall-clock text ("2026-10-29T09:00") for a datetime-local input, whatever zone
 *  the browser is in (audit C13: slicing the UTC string showed UTC as if it were local time). */
export function mountainInputValue(iso: string | null | undefined): string {
  return toZoneLocalInput(iso, TIME_ZONE);
}

/** A datetime-local input's text, read as Mountain wall-clock time, back to a UTC ISO string ("" stays ""). */
export function mountainInputToISO(text: string): string {
  const m = /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})/.exec(text.trim());
  return m ? mountainToISO(m[1], m[2]) : "";
}

/** An instant as "YYYY-MM-DDTHH:MM" wall time in `timeZone`, for a datetime-local input
 *  (event dates are typed in the event's own zone; the server reads them the same way). */
export function toZoneLocalInput(iso: string | null | undefined, timeZone: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat("en-US", { timeZone, year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hourCycle: "h23" })
      .formatToParts(d)
      .map((p) => [p.type, p.value]),
  );
  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
}

/** A calendar date column (next charge, due date…) as "Nov 15, 2026". The API sends these as UTC midnight
 *  ("2026-11-15T00:00:00Z"); reading that in Mountain Time would show the day before, so only the date part is used. */
export function formatDay(value: string | null | undefined): string {
  if (!value) return "—";
  const m = /^(\d{4}-\d{2}-\d{2})/.exec(value);
  return m ? formatDate(m[1]) : formatDate(value);
}
